import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  incidentTypes,
  incidentLocations,
  notificationChannelOptions,
  getTodayStr,
  getNowTimestamp,
  getIncidentTypeById,
  type IncidentLog,
  type NotificationChannel,
} from '@/mocks/preInspection';

interface IncidentModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  className: string;
  onSave: (log: IncidentLog) => void;
  existingLogs: IncidentLog[];
  userRole: string;
  userName: string;
}

export default function IncidentModal({
  open,
  onClose,
  studentId,
  studentName,
  className,
  onSave,
  existingLogs,
  userRole,
  userName,
}: IncidentModalProps) {
  const { t } = useTranslation();
  const [incidentTypeId, setIncidentTypeId] = useState('');
  const [severity, setSeverity] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [actionsTaken, setActionsTaken] = useState('');
  const [staffInvolved, setStaffInvolved] = useState('');
  const [parentNotified, setParentNotified] = useState(false);
  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([]);
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [saved, setSaved] = useState(false);
  const [viewTab, setViewTab] = useState<'form' | 'history'>('form');

  const selectedType = incidentTypeId ? getIncidentTypeById(incidentTypeId) : null;

  const studentLogs = existingLogs
    .filter((l) => l.studentId === studentId)
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  const toggleNotificationChannel = (ch: NotificationChannel) => {
    setNotificationChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    );
  };

  const handleSave = () => {
    if (!incidentTypeId || !severity || !location || !description) return;

    const log: IncidentLog = {
      id: `inc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      studentId,
      studentName,
      className,
      date: getTodayStr(),
      timestamp: getNowTimestamp(),
      incidentTypeId,
      severity,
      location,
      description,
      actionsTaken,
      staffInvolved: staffInvolved.split(',').map((s) => s.trim()).filter(Boolean),
      parentNotified,
      notificationChannels: parentNotified ? notificationChannels : [],
      followUpRequired,
      followUpNotes: followUpRequired ? followUpNotes : '',
      createdBy: userName,
    };

    onSave(log);
    setSaved(true);
  };

  const handleClose = () => {
    setIncidentTypeId('');
    setSeverity('');
    setLocation('');
    setDescription('');
    setActionsTaken('');
    setStaffInvolved('');
    setParentNotified(false);
    setNotificationChannels([]);
    setFollowUpRequired(false);
    setFollowUpNotes('');
    setSaved(false);
    setViewTab('form');
    onClose();
  };

  if (!open) return null;

  const renderHistoryTab = () => (
    <div className="flex flex-col gap-3">
      {studentLogs.length === 0 ? (
        <div className="py-10 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
            <i className="ri-alert-line text-foreground-300 text-xl" />
          </div>
          <p className="text-sm font-label text-foreground-500">{t('attendance.noIncidentLogs')}</p>
          <p className="text-xs text-foreground-400 mt-1">{t('attendance.noIncidentLogsHint')}</p>
        </div>
      ) : (
        studentLogs.map((log) => {
          const type = getIncidentTypeById(log.incidentTypeId);
          const severityColor: Record<string, string> = {
            Minor: 'bg-yellow-50 border-yellow-200 text-yellow-700',
            Moderate: 'bg-orange-50 border-orange-200 text-orange-700',
            Major: 'bg-red-50 border-red-200 text-red-700',
            Serious: 'bg-red-50 border-red-200 text-red-700',
            Critical: 'bg-red-100 border-red-300 text-red-800',
            Severe: 'bg-red-50 border-red-200 text-red-700',
            Attempt: 'bg-yellow-50 border-yellow-200 text-yellow-700',
            Brief: 'bg-orange-50 border-orange-200 text-orange-700',
            Extended: 'bg-red-50 border-red-200 text-red-700',
          };
          return (
            <div key={log.id} className="p-3 rounded-xl border border-background-200 bg-background-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50">
                    <i className={`${type?.icon || 'ri-alert-line'} text-red-600 text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-label font-medium text-foreground-800">{type?.label || log.incidentTypeId}</p>
                    <p className="text-[10px] text-foreground-400 font-label">
                      {new Date(log.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {' · '}
                      {new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-label font-medium rounded-full border ${severityColor[log.severity] || 'bg-background-100 text-foreground-600'}`}>
                  {log.severity}
                </span>
              </div>

              <div className="flex flex-wrap gap-1 mb-2">
                <span className="px-2 py-0.5 text-[10px] font-label rounded-full bg-background-100 text-foreground-500">{t('attendance.locationLabel')}: {log.location}</span>
                {log.staffInvolved.length > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-label rounded-full bg-background-100 text-foreground-500">
                    {t('attendance.staffInvolvedLabel')}: {log.staffInvolved.join(', ')}
                  </span>
                )}
              </div>

              <p className="text-xs text-foreground-600 font-label mb-1.5 leading-relaxed">{log.description}</p>

              {log.actionsTaken && (
                <p className="text-xs text-foreground-500 font-label mb-1.5 leading-relaxed">
                  <span className="font-medium">{t('attendance.actionsTakenLabel')}:</span> {log.actionsTaken}
                </p>
              )}

              <div className="flex items-center gap-3 text-[10px] text-foreground-400 font-label">
                <span>{t('attendance.recordedBy')}: {log.createdBy}</span>
                {log.parentNotified && (
                  <span className="flex items-center gap-1 text-green-600">
                    <i className="ri-check-double-line text-xs" />
                    {t('attendance.parentNotified')}
                  </span>
                )}
                {log.followUpRequired && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <i className="ri-flag-line text-xs" />
                    {t('attendance.followUpRequired')}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const renderFormTab = () => (
    <div className="flex flex-col gap-5">
      {/* Incident Type */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.incidentType')} <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {incidentTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => {
                setIncidentTypeId(type.id);
                setSeverity('');
              }}
              className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                incidentTypeId === type.id
                  ? 'border-red-300 bg-red-50 shadow-sm'
                  : 'border-background-200 bg-background-50 hover:bg-background-100'
              }`}
            >
              <i className={`${type.icon} text-xl ${incidentTypeId === type.id ? 'text-red-600' : 'text-foreground-500'}`} />
              <span className="text-[10px] font-label font-medium text-foreground-700 text-center leading-tight">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Severity */}
      {selectedType && (
        <div>
          <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
            {t('attendance.severityLevel')} <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {selectedType.severityLevels.map((lvl) => (
              <button
                key={lvl}
                onClick={() => setSeverity(lvl)}
                className={`px-3 py-2 rounded-lg text-xs font-label border transition-all cursor-pointer whitespace-nowrap ${
                  severity === lvl
                    ? lvl === 'Critical' || lvl === 'Severe'
                      ? 'bg-red-500 border-red-500 text-white font-medium'
                      : 'bg-red-100 border-red-300 text-red-800 font-medium'
                    : 'bg-background-50 border-background-200 text-foreground-600 hover:bg-background-100'
                }`}
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Location */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.incidentLocation')} <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {incidentLocations.map((loc) => (
            <button
              key={loc}
              onClick={() => setLocation(loc)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-label border transition-all cursor-pointer whitespace-nowrap ${
                location === loc
                  ? 'bg-accent-100 border-accent-300 text-accent-800 font-medium'
                  : 'bg-background-50 border-background-200 text-foreground-600 hover:bg-background-100'
              }`}
            >
              <i className="ri-map-pin-line text-sm" />
              {loc}
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.incidentDescription')} <span className="text-red-500">*</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('attendance.incidentDescriptionPlaceholder')}
          maxLength={500}
          rows={3}
          className="w-full px-3 py-2.5 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400 resize-none"
        />
        <span className="text-[10px] text-foreground-400 font-label mt-1 block text-right">{description.length}/500</span>
      </div>

      {/* Actions Taken */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.actionsTaken')}
        </label>
        <textarea
          value={actionsTaken}
          onChange={(e) => setActionsTaken(e.target.value)}
          placeholder={t('attendance.actionsTakenPlaceholder')}
          maxLength={500}
          rows={2}
          className="w-full px-3 py-2.5 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400 resize-none"
        />
      </div>

      {/* Staff Involved */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.staffInvolved')}
        </label>
        <input
          type="text"
          value={staffInvolved}
          onChange={(e) => setStaffInvolved(e.target.value)}
          placeholder={t('attendance.staffInvolvedPlaceholder')}
          className="w-full px-3 py-2.5 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400"
        />
      </div>

      {/* Follow-up */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-background-50 border border-background-200">
        <div>
          <p className="text-sm font-label font-semibold text-foreground-800">{t('attendance.followUpRequiredQuestion')}</p>
          <p className="text-[11px] text-foreground-400 font-label">{t('attendance.followUpRequiredHint')}</p>
        </div>
        <button
          onClick={() => setFollowUpRequired(!followUpRequired)}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap ${
            followUpRequired ? 'bg-accent-500' : 'bg-background-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 shadow-sm ${
              followUpRequired ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {followUpRequired && (
        <div>
          <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
            {t('attendance.followUpNotes')}
          </label>
          <textarea
            value={followUpNotes}
            onChange={(e) => setFollowUpNotes(e.target.value)}
            placeholder={t('attendance.followUpNotesPlaceholder')}
            maxLength={500}
            rows={2}
            className="w-full px-3 py-2.5 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400 resize-none"
          />
        </div>
      )}

      {/* Parent Notification */}
      <div className="border-t border-background-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-label font-semibold text-foreground-800">{t('attendance.notifyParent')}</p>
            <p className="text-[11px] text-foreground-400 font-label">{t('attendance.incidentNotifyParentHint')}</p>
          </div>
          <button
            onClick={() => setParentNotified(!parentNotified)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              parentNotified ? 'bg-semantic-success' : 'bg-background-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 shadow-sm ${
                parentNotified ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {parentNotified && (
          <div className="p-3 rounded-xl bg-background-50 border border-background-200">
            <p className="text-xs font-label font-medium text-foreground-600 mb-2">{t('attendance.sendVia')}</p>
            <div className="flex flex-wrap gap-2">
              {notificationChannelOptions.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => toggleNotificationChannel(ch.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-label border transition-all cursor-pointer whitespace-nowrap ${
                    notificationChannels.includes(ch.id)
                      ? 'bg-accent-100 border-accent-300 text-accent-800 font-medium'
                      : 'bg-white border-background-200 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${ch.icon} text-sm`} />
                  {ch.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center gap-3 pt-2 border-t border-background-200">
        <button
          onClick={handleClose}
          className="px-4 py-2.5 text-sm font-label font-medium rounded-lg border border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={!incidentTypeId || !severity || !location || !description}
          className="flex-1 px-4 py-2.5 text-sm font-label font-medium rounded-lg bg-semantic-error text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t('attendance.saveIncident')}
        </button>
      </div>
    </div>
  );

  const renderSavedState = () => (
    <div className="py-10 text-center">
      <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-green-50 mb-4">
        <i className="ri-check-double-line text-green-500 text-3xl" />
      </div>
      <p className="text-base font-label font-semibold text-foreground-800 mb-1">{t('attendance.incidentSaved')}</p>
      <p className="text-sm text-foreground-500 font-label mb-5">{t('attendance.incidentSavedHint', { name: studentName })}</p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => {
            setSaved(false);
            setIncidentTypeId('');
            setSeverity('');
            setLocation('');
            setDescription('');
            setActionsTaken('');
            setStaffInvolved('');
            setParentNotified(false);
            setNotificationChannels([]);
            setFollowUpRequired(false);
            setFollowUpNotes('');
          }}
          className="px-4 py-2 text-sm font-label font-medium rounded-lg bg-accent-100 text-accent-800 border border-accent-200 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('attendance.logAnother')}
        </button>
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm font-label font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('common.close')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-background-50 rounded-2xl shadow-floating max-w-2xl w-full max-h-[90vh] flex flex-col animate-[modalEnter_200ms_cubic-bezier(0.4,0,0.2,1)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50">
              <i className="ri-alert-line text-red-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-heading font-700 text-foreground-900">{t('attendance.classroomIncidentEmergency')}</h2>
              <p className="text-xs text-foreground-400 font-label">
                {studentName} — {className}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex items-center bg-background-100 rounded-full p-1 mr-2">
              <button
                onClick={() => setViewTab('form')}
                className={`px-3 py-1.5 text-xs font-label font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  viewTab === 'form'
                    ? 'bg-white text-foreground-800 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {t('attendance.newIncident')}
              </button>
              <button
                onClick={() => setViewTab('history')}
                className={`px-3 py-1.5 text-xs font-label font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  viewTab === 'history'
                    ? 'bg-white text-foreground-800 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {t('attendance.incidentHistory')}
                {studentLogs.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-accent-100 text-accent-700">{studentLogs.length}</span>
                )}
              </button>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {saved ? renderSavedState() : viewTab === 'history' ? renderHistoryTab() : renderFormTab()}
        </div>
      </div>
    </div>
  );
}